import { useState, useCallback } from 'react';
import axios from 'axios';
import { inZone } from '../utils/companyDate';
import { useCompanyTimezone } from '../context/AuthContext';

// The "N new signups from your intake form" notice, shared by the Dashboard and
// the Customers page. A signup is listed until the owner reviews it: saves its
// edit form, or presses Mark reviewed here. Both stamp
// customers.intake_reviewed_at (migration 078), and
// GET /api/customers/intake-signups lists exactly the parents not yet stamped.
//
// The pages own what Review does: the Dashboard opens the edit form on the
// Customers page, the Customers page scrolls to the card it already shows.

// The list, its load, and Mark reviewed. setItems is exposed for the Dashboard,
// which loads the list inside its own Promise.all.
export function useIntakeSignups() {
  const [items, setItems] = useState([]);
  const [reviewingId, setReviewingId] = useState(null);
  const [error, setError] = useState('');

  // A failure hides the notice rather than reaching the page around it.
  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers/intake-signups`);
      setItems(Array.isArray(response?.data?.items) ? response.data.items : []);
    } catch (err) {
      setItems([]);
    }
  }, []);

  // Removed from the notice only once the server has recorded it, so a failed
  // request leaves the row where it was. Resolves true when it was recorded.
  const markReviewed = useCallback(async (item) => {
    setReviewingId(item.id);
    setError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/customers/${item.id}/intake-reviewed`);
      setItems((prev) => prev.filter((row) => row.id !== item.id));
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Could not mark that signup reviewed. Please try again.');
      return false;
    } finally {
      setReviewingId(null);
    }
  }, []);

  return { items, setItems, reviewingId, error, load, markReviewed };
}

const formatSignedUp = (value, timezone) => (value
  ? new Date(value).toLocaleDateString('en-US', inZone(timezone, { month: 'short', day: 'numeric', year: 'numeric' }))
  : '-');

// Absent when there is nothing to review. The pages decide who sees it (owner
// and admin, like the endpoint).
export default function IntakeSignupsNotice({ items, error, reviewingId, onReview, onMarkReviewed }) {
  const timezone = useCompanyTimezone();
  if (!items || items.length === 0) return null;
  return (
    <section className="intake-signups-card" aria-labelledby="intake-signups-title">
      <h2 id="intake-signups-title" className="intake-signups-title">
        {items.length} new {items.length === 1 ? 'signup' : 'signups'} from your intake form
      </h2>
      <p className="intake-signups-intro">
        Check each one and save it, or mark it reviewed if nothing needs changing.
      </p>
      {error && (
        <p className="intake-signups-error" role="alert">{error}</p>
      )}
      <ul className="intake-signups-list">
        {items.map((item) => (
          <li key={item.id} className="intake-signup-row">
            <div className="intake-signup-info">
              <div className="intake-signup-name">
                {item.name || 'Unnamed customer'}
                {item.intakeStatus === 'waitlisted' && item.isActive === false && (
                  <span className="intake-signup-tag">Held</span>
                )}
                {item.intakeStatus !== 'waitlisted' && item.isActive === false && (
                  <span className="intake-signup-tag">Archived</span>
                )}
              </div>
              <div className="intake-signup-address">
                {item.address}
                {item.propertyCount > 0 && ` + ${item.propertyCount} more ${item.propertyCount === 1 ? 'property' : 'properties'}`}
              </div>
              <dl className="intake-signup-facts">
                <div><dt>Service</dt><dd>{item.servicePreference === 'seasonal' ? 'Seasonal' : 'On-Call'}</dd></div>
                <div>
                  <dt>Driveway</dt>
                  <dd>
                    {item.drivewaySize || 'Not sure'}
                    {!item.sizeConfirmed && ' (unconfirmed)'}
                  </dd>
                </div>
                <div>
                  <dt>Salt</dt>
                  <dd>
                    {item.saltPreference === 'always' ? 'Always'
                      : item.saltPreference === 'as_needed' ? 'As needed'
                        : 'No'}
                  </dd>
                </div>
                <div>
                  <dt>Signed up</dt>
                  <dd>{formatSignedUp(item.createdAt, timezone)}</dd>
                </div>
              </dl>
            </div>
            <div className="intake-signup-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => onReview(item)}
              >
                Review
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => onMarkReviewed(item)}
                disabled={reviewingId === item.id}
              >
                {reviewingId === item.id ? 'Saving...' : 'Mark reviewed'}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <style>{styles}</style>
    </section>
  );
}

const styles = `
  .intake-signups-card {
    background: #fffbeb;
    border: 1px solid #fcd34d;
    color: #78350f;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 16px;
  }
  .intake-signups-title {
    font-size: 1.05rem;
    margin: 0 0 4px;
    color: #92400e;
  }
  .intake-signups-intro {
    margin: 0 0 12px;
    font-size: 0.9rem;
  }
  .intake-signups-error {
    margin: 0 0 12px;
    color: var(--color-error);
    font-weight: 600;
  }
  .intake-signups-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .intake-signup-row {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1px solid #fde68a;
    border-radius: 8px;
    padding: 12px;
  }
  .intake-signup-info {
    flex: 1;
    min-width: 0;
  }
  .intake-signup-name {
    font-weight: 700;
    color: #1f2937;
  }
  .intake-signup-tag {
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    background: #e5e7eb;
    color: #374151;
  }
  .intake-signup-address {
    font-size: 0.85rem;
    color: #4b5563;
    overflow-wrap: anywhere;
  }
  .intake-signup-facts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin: 6px 0 0;
    font-size: 0.8rem;
  }
  .intake-signup-facts div {
    display: flex;
    gap: 4px;
  }
  .intake-signup-facts dt {
    color: #6b7280;
  }
  .intake-signup-facts dt::after {
    content: ':';
  }
  .intake-signup-facts dd {
    margin: 0;
    color: #1f2937;
    font-weight: 600;
  }
  .intake-signup-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex-shrink: 0;
  }
  @media (max-width: 600px) {
    .intake-signup-row {
      flex-direction: column;
      align-items: stretch;
    }
    .intake-signup-actions {
      flex-direction: row;
    }
    .intake-signup-actions .btn {
      flex: 1;
      min-height: 44px;
    }
  }
`;
